// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — useOrgProfiles hook
// ─────────────────────────────────────────────────────────────────────────────
// Fetches all active profiles in the current organization.
// Used for the Seller field dropdown on the Sale Form.
// RLS policy on profiles allows all org members to SELECT profiles
// in their own org (confirmed: migration 00002_profiles.sql).
// ─────────────────────────────────────────────────────────────────────────────

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface OrgProfile {
  id: string;
  full_name: string;
  email: string | null;
  role: string | null;
}

export const ORG_PROFILES_KEY = 'org_profiles';

export function useOrgProfiles(assigneesOnly = false) {
  const { organization, can, access } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [ORG_PROFILES_KEY, orgId, access?.access_version, assigneesOnly],
    queryFn: async (): Promise<OrgProfile[]> => {
      if (assigneesOnly) {
        const { data, error } = await supabase.rpc('list_task_assignees');
        if (error) throw error;
        return data ?? [];
      }
      const { data, error } = await supabase
        .from('access_profiles')
        .select('id, full_name, email, role')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .in('account_status', ['active', 'onboarding'])
        .order('full_name');

      if (error) throw error;
      return (data as OrgProfile[]) ?? [];
    },
    enabled:
      !!orgId && (assigneesOnly ? can('plans.view') : can('lookup.sellers') || can('plans.view')),
    staleTime: 1000 * 60 * 5, // 5 min — profiles don't change often
  });
}
